// src/eventslive/EventsLiveBanner.tsx — the Home state-machine banner (BUILD_PLAN
// PR I-2, extended for multiple events). One slot on HomePage, above the
// duty/roster furniture. Self-fetches the caller's open-call state and renders
// ONE banner per relevant event (a volunteer can be on several at once), each
// walking: call → pending/approved → live. Reuses the InfoHub striped-edge banner
// shape (cf. ConsentsBanner) in the EL (racing green + gold) lane.

import { useEffect, useState, type ReactNode } from "react";
import { C, F, EL } from "../theme";
import { ElIcon } from "./ElIcon";
import { ensureElKeyframes } from "./elKeyframes";
import { fmtDateRange, fmtDayChip } from "./elFmt";
import { fetchOpenCall, type OpenCallResponse, type ELEventEntry } from "../lib/events";

function StripeBanner({ grad, stripe, glow, onClick, children }: {
  grad: string; stripe: string; glow?: string; onClick: () => void; children: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        position: "relative", overflow: "hidden", width: "100%", textAlign: "left", border: "none",
        borderRadius: 16, padding: "14px 15px 14px 19px", background: grad, color: "#fff", cursor: "pointer",
        boxShadow: glow || "0 8px 22px rgba(10,74,58,0.26), inset 0 1px 0 rgba(255,255,255,0.14)",
        display: "flex", gap: 12, alignItems: "center", animation: "el-fade-up .4s ease both",
      }}
    >
      <div aria-hidden="true" style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 6, background: `repeating-linear-gradient(135deg, ${stripe} 0 6px, transparent 6px 12px)` }} />
      {children}
    </button>
  );
}

// One banner for one event, in its own state. Routes carry ?event=<id> so the
// Crew Call / Live Duty pages open the right event when the volunteer has several.
function SingleBanner({ entry, onNavigate }: { entry: ELEventEntry; onNavigate: (path: string) => void }) {
  const event = entry.event;
  const confirmed = entry.myConfirmed ?? [];
  const offer = entry.myOffer ?? null;
  // Prefer the server-derived state; fall back to deriving it client-side.
  const state = entry.state ?? (confirmed.length > 0 ? "live" : offer ? "pending" : entry.call?.isActive ? "call" : null);
  if (!state) return null;

  if (state === "call") {
    return (
      <StripeBanner grad={`linear-gradient(155deg, ${EL.base}, ${EL.deep})`} stripe={EL.goldGlow} onClick={() => onNavigate(`/crew-call?event=${event.id}`)}>
        <span style={{ width: 42, height: 42, borderRadius: 12, background: "rgba(240,217,138,0.16)", border: "1px solid rgba(240,217,138,0.34)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <ElIcon name="train" size={22} color={EL.goldGlow} />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: "block", fontFamily: F.body, fontSize: 10, fontWeight: 800, letterSpacing: 1.3, textTransform: "uppercase", color: "rgba(240,217,138,0.85)" }}>Volunteers needed</span>
          <span style={{ display: "block", fontFamily: F.serif, fontSize: 19, fontWeight: 700, letterSpacing: -0.3, lineHeight: 1.1, marginTop: 2 }}>{event.name}</span>
          <span style={{ display: "block", fontFamily: F.body, fontSize: 12.5, fontWeight: 600, color: "rgba(255,255,255,0.86)", marginTop: 3 }}>{fmtDateRange(event.days)} · tap to see roles &amp; offer your time</span>
        </span>
        <span style={{ fontSize: 22, fontWeight: 700, opacity: 0.85 }} aria-hidden="true">→</span>
      </StripeBanner>
    );
  }

  if (state === "pending") {
    const approved = offer?.status === "approved";
    return (
      <div style={{ background: "#EFF4EB", border: `1px solid ${C.sageLt}`, borderRadius: 18, boxShadow: C.shadowS, padding: 16 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <span style={{ width: 40, height: 40, borderRadius: 11, background: C.sageBg, border: `1px solid ${C.sageLt}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <ElIcon name="clock" size={20} color={C.sageDk} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: F.body, fontSize: 10.5, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase", color: C.sageDk }}>{approved ? "You're approved" : "Availability sent"}</div>
            <div style={{ fontFamily: F.serif, fontSize: 16, fontWeight: 700, color: C.ink, marginTop: 1 }}>You're on the list for {event.name}</div>
            <div style={{ fontFamily: F.body, fontSize: 12.5, color: C.inkSoft, marginTop: 2 }}>{approved ? "Approved — the Event Manager is finalising your duties." : "The Event Manager is reviewing — we'll let you know once your role is confirmed."}</div>
          </div>
        </div>
        <button onClick={() => onNavigate(`/crew-call?event=${event.id}`)} style={{ marginTop: 12, width: "100%", border: `1px solid ${C.sageLt}`, background: C.card, color: C.sageDk, borderRadius: 11, padding: "9px", fontFamily: F.body, fontWeight: 800, fontSize: 13, cursor: "pointer" }}>View / edit what I offered</button>
      </div>
    );
  }

  // state === "live"
  const roleLabel = [...new Set(confirmed.map((a) => a.roleName))].join(" + ");
  const days = [...new Set(confirmed.map((a) => a.date))].sort();
  const signedOn = !!entry.signedOn;
  const dayLabel = days.length === event.days.length ? "All weekend" : days.map(fmtDayChip).join(" · ");
  return (
    <StripeBanner
      grad={`linear-gradient(150deg, ${EL.base} 0%, ${EL.deep} 100%)`} stripe={EL.goldGlow} onClick={() => onNavigate(`/live-duty?event=${event.id}`)}
      glow="0 10px 26px rgba(10,74,58,0.34), inset 0 1px 0 rgba(255,255,255,0.16)"
    >
      <span style={{ width: 46, height: 46, borderRadius: 13, background: "rgba(240,217,138,0.16)", border: "1px solid rgba(240,217,138,0.36)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, position: "relative" }}>
        <ElIcon name="shieldCheck" size={24} color={EL.goldGlow} />
        {signedOn && <span style={{ position: "absolute", top: -3, right: -3, width: 12, height: 12, borderRadius: "50%", background: EL.connGreen, border: "2px solid #073629", animation: "el-conn 2s infinite" }} />}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <span style={{ fontFamily: F.body, fontSize: 10, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase", color: EL.goldGlow }}>Events Live</span>
          <span style={{ width: 3, height: 3, borderRadius: "50%", background: "rgba(240,217,138,0.6)" }} />
          <span style={{ fontFamily: F.body, fontSize: 10, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase", color: signedOn ? EL.connGreen : "rgba(255,255,255,0.7)" }}>{signedOn ? "On duty now" : "Ready"}</span>
        </span>
        <span style={{ display: "block", fontFamily: F.serif, fontSize: 18, fontWeight: 700, letterSpacing: -0.3, lineHeight: 1.12, marginTop: 3 }}>{event.name}</span>
        <span style={{ display: "block", fontFamily: F.body, fontSize: 12.5, fontWeight: 700, color: "#fff", marginTop: 3 }}>You're on as {roleLabel}</span>
        <span style={{ display: "block", fontFamily: F.body, fontSize: 11.5, fontWeight: 600, color: "rgba(255,255,255,0.78)", marginTop: 1 }}>{dayLabel} · tap to open your duty view</span>
      </span>
      <span style={{ fontSize: 22, fontWeight: 700, color: EL.goldGlow }} aria-hidden="true">→</span>
    </StripeBanner>
  );
}

export function EventsLiveBanner({ onNavigate }: { onNavigate: (path: string) => void }) {
  const [data, setData] = useState<OpenCallResponse | null>(null);

  useEffect(() => {
    ensureElKeyframes();
    let alive = true;
    const load = () => fetchOpenCall().then((d) => { if (alive) setData(d); }).catch(() => { if (alive) setData({ call: null }); });
    load();
    const id = window.setInterval(load, 60_000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { alive = false; window.clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, []);

  if (!data) return null;

  // Prefer the multi-event list; fall back to a single synthesised entry from the
  // top-level fields if an older API is still serving (deploy-gap safety).
  const entries: ELEventEntry[] = data.events ?? (data.event
    ? [{
        event: data.event,
        call: data.call,
        roles: data.roles ?? [],
        myOffer: data.myOffer ?? null,
        myAssignments: data.myAssignments ?? [],
        myConfirmed: data.myConfirmed ?? [],
        signedOn: !!data.signedOn,
        signedOnAt: data.signedOnAt ?? null,
        state: null,
      }]
    : []);

  const shown = entries.filter((e) => (e.state ?? (e.myConfirmed?.length ? "live" : e.myOffer ? "pending" : e.call?.isActive ? "call" : null)) != null);
  if (shown.length === 0) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {shown.map((entry) => <SingleBanner key={entry.event.id} entry={entry} onNavigate={onNavigate} />)}
    </div>
  );
}
